import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  KeyRound,
  Loader2,
  MoreHorizontal,
  Pencil,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@sportscanner/ui";
import { apiDelete, apiGet, apiPost, getApiErrorCode } from "@/lib/api";
import { useMe, type Permission } from "@/lib/me";
import ConfirmDialog from "@/components/confirm-dialog";
import EditProfileDialog from "./edit-profile-dialog";
import EditPermissionsDialog from "./edit-permissions-dialog";

export interface AdminRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: "MANAGER" | "STAFF";
  inviteStatus: "INVITED" | "ACTIVE";
  emailVerified: boolean;
  permissions: Partial<Record<Permission, boolean>> | null;
}

interface AdminsResponse {
  admins: AdminRow[];
}

export default function AdminsList({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useMe();
  const actorId = user?.id ?? null;

  const [editing, setEditing] = useState<AdminRow | null>(null);
  const [editingPerms, setEditingPerms] = useState<AdminRow | null>(null);
  const [resetting, setResetting] = useState<AdminRow | null>(null);
  const [deleting, setDeleting] = useState<AdminRow | null>(null);

  const adminsQuery = useQuery({
    queryKey: ["admin", "admins", clubId],
    queryFn: () => apiGet<AdminsResponse>(`/api/admin/clubs/${clubId}/admins`),
    placeholderData: keepPreviousData,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["admin", "admins", clubId],
    });

  const resetMutation = useMutation({
    mutationFn: (row: AdminRow) =>
      apiPost<{ ok: true }>(
        `/api/admin/clubs/${clubId}/admins/${row.id}/password-reset`,
        {},
      ),
    onSuccess: (_, row) => {
      toast.success(t("admins.toast.resetSent", { email: row.email }));
      setResetting(null);
    },
    onError: (err) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (row: AdminRow) =>
      apiDelete<{ ok: true }>(`/api/admin/clubs/${clubId}/admins/${row.id}`),
    onSuccess: () => {
      toast.success(t("admins.toast.deleted"));
      setDeleting(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  if (adminsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (adminsQuery.error) {
    return (
      <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
        <p className="text-sm text-neutral-500">
          {t("errors.api.INTERNAL_ERROR")}
        </p>
      </div>
    );
  }

  const admins = adminsQuery.data?.admins ?? [];

  if (admins.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-200 px-6 py-10 text-center">
        <p className="text-sm text-neutral-500">{t("admins.empty")}</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <Th>{t("admins.cols.name")}</Th>
                <Th>{t("admins.cols.email")}</Th>
                <Th>{t("admins.cols.phone")}</Th>
                <Th>{t("admins.cols.role")}</Th>
                <Th>{t("admins.cols.status")}</Th>
                <th className="px-4 py-2.5 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {admins.map((row) => {
                const isSelf = row.id === actorId;
                const displayName =
                  `${row.firstName} ${row.lastName}`.trim() || "—";
                return (
                  <tr key={row.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium text-neutral-800">
                      {displayName}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">{row.email}</td>
                    <td className="px-4 py-3 text-neutral-700">
                      {row.phone || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={
                          row.role === "MANAGER"
                            ? "border-primary-200 text-primary-700 bg-primary-50"
                            : "border-neutral-200 text-neutral-700"
                        }
                      >
                        {t(`roles.${row.role}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {row.inviteStatus === "INVITED" ? (
                        <Badge
                          variant="outline"
                          className="border-amber-200 text-amber-700 bg-amber-50"
                        >
                          {t("admins.status.invited")}
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-emerald-200 text-emerald-700 bg-emerald-50"
                        >
                          {t("admins.status.active")}
                        </Badge>
                      )}
                    </td>
                    <td className="px-2 py-3 text-right">
                      {isSelf ? (
                        <span
                          title={t("admins.selfHint")}
                          className="text-xs text-neutral-400 pr-2"
                        >
                          {t("admins.selfLabel")}
                        </span>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="cursor-pointer"
                              aria-label={t("admins.actionsLabel")}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => setEditing(row)}
                              className="cursor-pointer"
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              {t("admins.actions.editProfile")}
                            </DropdownMenuItem>
                            {row.role === "STAFF" && (
                              <DropdownMenuItem
                                onClick={() => setEditingPerms(row)}
                                className="cursor-pointer"
                              >
                                <ShieldCheck className="w-4 h-4 mr-2" />
                                {t("admins.actions.editPermissions")}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => setResetting(row)}
                              disabled={row.inviteStatus === "INVITED"}
                              className="cursor-pointer"
                            >
                              <KeyRound className="w-4 h-4 mr-2" />
                              {t("admins.actions.passwordReset")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleting(row)}
                              className="cursor-pointer text-red-600 focus:text-red-700"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t("admins.actions.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditProfileDialog
          clubId={clubId}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}

      {editingPerms && editingPerms.role === "STAFF" && (
        <EditPermissionsDialog
          clubId={clubId}
          row={editingPerms}
          onClose={() => setEditingPerms(null)}
          onSaved={() => {
            setEditingPerms(null);
            invalidate();
          }}
        />
      )}

      <ConfirmDialog
        open={!!resetting}
        onOpenChange={(o) => !o && setResetting(null)}
        title={t("admins.resetConfirmTitle")}
        description={
          resetting
            ? t("admins.resetConfirmBody", { email: resetting.email })
            : undefined
        }
        confirmLabel={t("admins.resetConfirmButton")}
        isPending={resetMutation.isPending}
        onConfirm={() => resetting && resetMutation.mutate(resetting)}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        variant="destructive"
        title={t("admins.deleteConfirmTitle")}
        description={
          deleting
            ? t("admins.deleteConfirmBody", {
                name:
                  `${deleting.firstName} ${deleting.lastName}`.trim() ||
                  deleting.email,
              })
            : undefined
        }
        confirmLabel={t("admins.deleteConfirmButton")}
        isPending={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting)}
      />
    </>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
      {children}
    </th>
  );
}
